import React, { useState, useEffect } from 'react';
import { Edit, Lightbulb, Loader2, Check, ExternalLink } from 'lucide-react';

const BackEndUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';

const NotesTab = ({ userNotes, setUserNotes, saveNotes, documentData, makeAuthenticatedRequest }) => {
  const [relatedNotes, setRelatedNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null, 'saving', 'saved', 'error'
  const docId = documentData?.id || documentData?.documentId;

  // Récupération des notes liées au même sujet
  const fetchRelatedNotes = async () => {
    if (!docId) return;

    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(
        `${BackEndUrl}/api/findSimilarDocuments/${docId}?includeNotes=true`
      );

      const data = await response.json();
      
      // Filtrer uniquement les documents avec des notes
      const notesFromSimilar = data.results && Array.isArray(data.results)
        ? data.results
            .filter(doc => doc.document?.metadata?.userNotes)
            .map(doc => ({
              notes: doc.document.metadata.userNotes,
              title: doc.document.metadata.title || doc.document.fileName || 'Document sans titre',
              similarity: doc.similarity?.totalScore || 0.5,
              documentId: doc.document.id || doc.document.documentId
            }))
        : [];

      setRelatedNotes(notesFromSimilar);
    } catch (error) {
      console.error('Erreur lors de la récupération des notes similaires:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRelatedNotes();
  }, [docId]);

  // Sauvegarder les notes avec feedback visuel
  const handleSaveNotes = async () => {
    setSaveStatus('saving');
    try {
      await saveNotes();
      setSaveStatus('saved');
      
      // Réinitialiser après 2 secondes
      setTimeout(() => {
        setSaveStatus(null);
      }, 2000);
    } catch (error) {
      setSaveStatus('error');
      
      // Réinitialiser après 3 secondes
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    }
  };

  return (
    <div className="space-y-8">
      {/* Zone de saisie des notes */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Edit className="h-5 w-5 text-blue-500" />
            <h3 className="text-lg font-medium text-gray-900">Vos notes personnelles</h3>
          </div>
          <button
            onClick={handleSaveNotes}
            disabled={saveStatus === 'saving'}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              saveStatus === 'saved'
                ? 'bg-green-100 text-green-700'
                : saveStatus === 'error'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            {saveStatus === 'saving' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sauvegarde...
              </>
            ) : saveStatus === 'saved' ? (
              <>
                <Check className="h-4 w-4" />
                Sauvegardé
              </>
            ) : saveStatus === 'error' ? (
              'Erreur'
            ) : (
              'Sauvegarder'
            )}
          </button>
        </div>

        <textarea
          value={userNotes}
          onChange={(e) => setUserNotes(e.target.value)}
          className="w-full h-40 p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          placeholder="Saisissez vos notes personnelles sur ce document ici..."
        />
      </div>

      {/* Suggestions d'améliorations */}
      {documentData?.analysis?.suggestions?.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            <h3 className="text-lg font-medium text-gray-900">Suggestions pour vos notes</h3>
          </div>
          
          <div className="space-y-3">
            {documentData.analysis.suggestions.map((suggestion, index) => (
              <div 
                key={index}
                className="p-4 bg-amber-50 border border-amber-100 rounded-lg"
              >
                <p className="text-amber-800">{suggestion}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes liées */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : relatedNotes.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-5 w-5 text-blue-500" />
            <h3 className="text-lg font-medium text-gray-900">Notes de documents similaires</h3>
          </div>
          
          <div className="space-y-4">
            {relatedNotes.map((note, index) => (
              <div 
                key={index}
                className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-blue-700">{note.title}</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Similarité: {Math.round(note.similarity * 100)}%
                    </p>
                  </div>
                  <a
                    href={`#document-${note.documentId}`} // Lien fictif, à adapter selon votre routing
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Voir
                  </a>
                </div>
                <p className="text-gray-700 whitespace-pre-line">{note.notes}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && relatedNotes.length === 0 && !documentData?.analysis?.suggestions?.length && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-600">
            Pas de notes associées ou de suggestions disponibles pour ce document.
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Soyez le premier à ajouter des notes intéressantes !
          </p>
        </div>
      )}
    </div>
  );
};

export default NotesTab;